/**
 * @file 逐位数字滚轮组件
 * 切换订阅类型时，价格/积分数字逐位滚动变化
 */
import React, {useEffect, useMemo, useState} from 'react';
import styles from './RollingNumber.module.less';

interface DigitRollerProps {
    digit: number;
    delay: number;
}

/**
 * 单个数字滚轮：0-9 纵向排列，通过 translateY 定位到目标数字
 */
const DigitRoller: React.FC<DigitRollerProps> = ({digit, delay}) => {
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        setCurrent(digit);
    }, [digit]);

    const offset = -current * 10;

    return (
        <span className={styles.digitRoller}>
            <span
                className={styles.digitStrip}
                style={{
                    transform: `translateY(${offset}%)`,
                    transitionDelay: `${delay}ms`,
                }}
            >
                {Array.from({length: 10}, (_, i) => (
                    <span key={i} className={styles.digitItem}>
                        {i}
                    </span>
                ))}
            </span>
        </span>
    );
};

interface RollingNumberProps {
    value: string | number;
}

/**
 * 数字滚轮组件：将数值拆分为单个字符，数字用滚轮，其他字符静态展示
 * 动画从左往右依次触发，产生逐位滚动的视觉效果
 */
const RollingNumber: React.FC<RollingNumberProps> = ({value}) => {
    const chars = useMemo(() => String(value).split(''), [value]);

    const digitIndices = useMemo(() => {
        const indices: number[] = [];
        chars.forEach((char, i) => {
            if (/\d/.test(char)) {
                indices.push(i);
            }
        });
        return indices;
    }, [chars]);

    const getDelay = (index: number) => {
        const pos = digitIndices.indexOf(index);
        return pos * 30;
    };

    return (
        <span className={styles.rollingNumber}>
            {chars.map((char, index) => {
                if (/\d/.test(char)) {
                    return (
                        // eslint-disable-next-line react/no-array-index-key -- 位置稳定的 key，复用 DOM 触发 transition
                        <span key={index} className={styles.charWrapper}>
                            <DigitRoller digit={Number(char)} delay={getDelay(index)} />
                        </span>
                    );
                }
                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={index} className={styles.staticChar}>
                        {char}
                    </span>
                );
            })}
        </span>
    );
};

export default RollingNumber;
